/*
  Tabs above 750px, accordions below.

  Progressive enhancement over <details> elements that ship open: with JavaScript off you
  get plain, working, open disclosures and nothing is unreachable. This file closes them,
  builds the tab strip from each panel's data-shintop-title, and swaps ARIA roles when the
  viewport crosses the breakpoint.

  A tab strip at 375px is bad for reading and worse for a screen reader, which is why the
  mode is viewport-driven rather than a merchant setting.
*/
if (!customElements.get('shintop-tabs')) {
  customElements.define(
    'shintop-tabs',
    class ShintopTabs extends HTMLElement {
      connectedCallback() {
        this.strip = this.querySelector('.shintop-tabs__strip');
        this.panels = Array.from(this.querySelectorAll('.shintop-tabs__panel'));
        if (this.panels.length === 0) return;

        // ACCORDION AT EVERY WIDTH (2026-09-06, the in-column layout). The element is rendered
        // with `data-layout="accordion"` and no strip; the same markup, the same panels, no tab
        // mode at any width. A missing strip means the same thing, so a layout that forgot the
        // attribute still degrades to an accordion rather than to a pile of open disclosures.
        // A DECLARED LAYOUT WINS OVER THE STRIP PROBE. 'sheet' (2026-09-07) and 'accordion'
        // both ship without a strip, so the `!this.strip` fallback below would silently
        // rewrite 'sheet' to 'accordion' and open only the first panel on a desktop.
        const declared = this.dataset.layout;
        this.layout =
          declared === 'accordion' || declared === 'sheet' ? declared : !this.strip ? 'accordion' : 'tabs';

        // A strip with one tab on it is not a tab strip, it is an orphan. A lone panel
        // stays a plain open disclosure at every width.
        this.multi = this.panels.length > 1;
        if (this.multi && this.layout === 'tabs') this.buildStrip();

        this.mq = window.matchMedia('(min-width: 750px)');
        this.onChange = () => this.applyMode();
        this.mq.addEventListener('change', this.onChange);

        // The sliding indicator is positioned in pixels, so it must follow reflow.
        this.onResize = () => {
          if (this.isTabs()) this.moveIndicator(this.tabs[this.activeIndex()]);
        };
        window.addEventListener('resize', this.onResize);

        this.panels.forEach((panel) => {
          panel.addEventListener('toggle', () => {
            if (this.isTabs() || !panel.open) return;
            // Accordion mode stays multi-open; nothing to do. Kept as a hook so the
            // behaviour is explicit rather than accidental.
          });
        });

        this.applyMode();
      }

      disconnectedCallback() {
        this.mq?.removeEventListener('change', this.onChange);
        window.removeEventListener('resize', this.onResize);
      }

      isTabs() {
        return this.layout === 'tabs' && this.multi && this.mq.matches;
      }

      buildStrip() {
        this.tabs = this.panels.map((panel, index) => {
          const tab = document.createElement('button');
          tab.type = 'button';
          tab.className = 'shintop-tabs__tab';
          // Read the heading from the DOM, not from a data- attribute. The attribute round
          // trip is what double-escaped "Allergens & label" in v1; textContent is already
          // decoded by the parser, so there is no encoding left to get wrong.
          const heading = panel.querySelector('.shintop-tabs__summary-title');
          tab.textContent = heading?.textContent.trim() || `Tab ${index + 1}`;
          tab.id = `${panel.id}-tab`;
          tab.setAttribute('aria-controls', panel.id);
          tab.addEventListener('click', () => this.select(index));
          tab.addEventListener('keydown', (event) => this.onKeydown(event, index));
          this.strip.appendChild(tab);
          return tab;
        });

        // One underline that MOVES between tabs, rather than a border that repaints on
        // each. Purely decorative; selection is still announced via aria-selected.
        this.indicator = document.createElement('span');
        this.indicator.className = 'shintop-tabs__indicator';
        this.indicator.setAttribute('aria-hidden', 'true');
        this.strip.appendChild(this.indicator);
      }

      moveIndicator(tab) {
        if (!this.indicator || !tab) return;
        this.indicator.style.width = `${tab.offsetWidth}px`;
        this.indicator.style.transform = `translateX(${tab.offsetLeft}px)`;
      }

      applyMode() {
        const tabsMode = this.isTabs();

        /*
          S08-02 (WCAG 2.4.3 Focus Order): REMEMBER WHERE FOCUS IS BEFORE ANYTHING IS HIDDEN.

          Crossing 750px hides one of this component's two control sets — the strip going
          down, every <summary> going up — and hiding the ancestor of the focused element
          blurs it. Measured 3/3 on the unfixed tree: focus on the "Ingredients" tab,
          narrow to 700, and document.activeElement is <body>. The reader is returned to
          the top of the document, mid-page, having pressed nothing.

          A rotated phone and a resized desktop window are the same event, so this is not
          a contrived path. The panel is the identity that survives the swap: the tab and
          the summary are two controls for it, and the one that exists after the swap is
          the one focus belongs on.
        */
        const wasFocused = document.activeElement;
        let focusedPanel = null;
        if (wasFocused && wasFocused !== document.body && this.contains(wasFocused)) {
          if (this.tabs && this.tabs.indexOf(wasFocused) !== -1) {
            focusedPanel = this.panels[this.tabs.indexOf(wasFocused)];
          } else if (wasFocused.closest) {
            const summary = wasFocused.closest('.shintop-tabs__summary');
            if (summary) focusedPanel = summary.closest('.shintop-tabs__panel');
          }
        }

        if (this.strip) this.strip.hidden = !tabsMode;

        this.panels.forEach((panel, index) => {
          const summary = panel.querySelector('.shintop-tabs__summary');

          if (tabsMode) {
            // The summary is redundant when the strip is visible: hide it from both the
            // pointer and the accessibility tree, and let the panel act as a tabpanel.
            panel.open = true;
            if (summary) summary.hidden = true;
            panel.setAttribute('role', 'tabpanel');
            panel.setAttribute('aria-labelledby', this.tabs[index].id);
            panel.hidden = index !== this.activeIndex();
          } else {
            if (summary) summary.hidden = false;
            panel.removeAttribute('role');
            panel.removeAttribute('aria-labelledby');
            panel.hidden = false;
            // COR-03: open the panel the shopper actually selected, not always the first. Narrowing
            // below the accordion breakpoint after picking e.g. "Reviews" used to jump back to 0.
            //
            // ...BUT NOT ON THE FIRST APPLICATION (2026-09-06). Naoya: "make about to be closed
            // as default." A phone arriving at the page should meet a closed strip of headings,
            // not a wall of description. `applied` is what tells those two cases apart: on load
            // at a phone width nothing has been selected yet, so nothing opens; every LATER
            // transition into accordion mode is a resize by someone who has been reading a tab,
            // and COR-03 keeps their place. Above 750px the branch above still opens every
            // panel, so About is the default tab with no click, which is settled (2026-09-01).
            if (this.layout === 'sheet') {
              // THE SHEET IS BAYMARD'S PAIR, ONE ELEMENT (2026-09-07). Naoya: "at least on
              // mobile, it should be kept as that tabs approach." Baymard's "Avoid Horizontal
              // Tabs" recommends *Expanded Sections* on desktop — where a long scroll is not an
              // obstacle and a tab hides content 29% of sites still hide — and *Collapsed
              // Sections* on a long mobile page. So: every panel open above 750px, every panel
              // closed below it. The <summary> stays a real control at both widths, which is why
              // this is not a desktop panel pretending to be undismissable.
              if (!this.applied) panel.open = this.mq.matches;
            } else if (this.layout === 'accordion') {
              // In-column accordion: the first panel (About) arrives open on a desktop, where
              // it sits beside the image and the column has the room; closed on a phone, per
              // the mobile ruling. Later applications leave the reader's open/closed state alone.
              if (!this.applied) panel.open = this.mq.matches && index === 0;
            } else if (!this.multi) {
              // S08-05 (audit 2026-09-13, chunk 9). A LONE PANEL IS LEFT AS AUTHORED, WHICH
              // IS OPEN. The rule is stated at the top of this file — "A strip with one tab
              // on it is not a tab strip, it is an orphan. A lone panel stays a plain open
              // disclosure at every width" — and until now nothing enforced it. With one
              // panel no strip is built, so `tabsMode` is false and control fell through to
              // the branch below, which closes every panel on the first application. The
              // result was a product whose single information panel arrived shut behind a
              // summary, on desktop as well as phone, with no strip to hint there was
              // anything to open.
              //
              // Doing nothing is the fix: these panels ship open in the markup, and this file
              // is progressive enhancement over that. Latent — unreachable while every
              // product has a vendor, because `maker` falls back to `product.vendor` and the
              // About panel therefore always renders alongside the others.
            } else {
              panel.open = this.applied ? index === this.activeIndex() : false;
            }
          }
        });

        // S08-02, the other half: hand focus to the surviving control for the SAME panel.
        // Going up, that means selecting the panel the reader had open, so they meet the tab
        // they were reading rather than a tab whose panel is hidden. Going down, the summary
        // is already un-hidden by the loop above. Untouched when nothing here had focus,
        // which is every ordinary load — `applied` is not involved, because the defect is a
        // transition and the first application is not one.
        const restore = focusedPanel ? this.panels.indexOf(focusedPanel) : -1;

        if (tabsMode) this.select(restore === -1 ? this.activeIndex() : restore, { focus: restore !== -1 });
        else if (restore !== -1) {
          const summary = focusedPanel.querySelector('.shintop-tabs__summary');
          if (summary) summary.focus({ preventScroll: true });
        }

        this.applied = true;
      }

      activeIndex() {
        const current = this.panels.findIndex((panel) => panel.dataset.shintopActive === 'true');
        return current === -1 ? 0 : current;
      }

      select(index, { focus = true } = {}) {
        this.panels.forEach((panel, i) => {
          const isActive = i === index;
          panel.dataset.shintopActive = String(isActive);
          if (this.isTabs()) panel.hidden = !isActive;

          const tab = this.tabs[i];
          tab.setAttribute('role', 'tab');
          tab.setAttribute('aria-selected', String(isActive));
          // Roving tabindex: only the selected tab is in the tab order, so a keyboard user
          // tabs past the strip in one stop and uses arrows within it.
          tab.tabIndex = isActive ? 0 : -1;
          tab.classList.toggle('shintop-tabs__tab--active', isActive);
        });

        this.moveIndicator(this.tabs[index]);
        if (focus && this.isTabs()) this.tabs[index].focus();
      }

      onKeydown(event, index) {
        const keys = {
          ArrowRight: index + 1,
          ArrowLeft: index - 1,
          Home: 0,
          End: this.tabs.length - 1,
        };
        if (!(event.key in keys)) return;

        event.preventDefault();
        const last = this.tabs.length - 1;
        let next = keys[event.key];
        if (next < 0) next = last;
        if (next > last) next = 0;
        this.select(next);
      }
    },
  );
}
